import { useState, useEffect, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { devOpsApiRef, DevOpsApi, CombinedReleaseHistory } from '@internal/backstage-plugin-ewii-azure';
import { ENVIRONMENTS } from '../utils';

export const useReleaseHistory = () => {
  const [releaseHistory, setReleaseHistory] = useState<Record<string, CombinedReleaseHistory[]>>({});
  const devOpsApi = useApi(devOpsApiRef) as DevOpsApi;

  const fetchReleaseHistory = useCallback(async () => {
    const releaseHistoryPromises = ENVIRONMENTS.map(async (env) => {
      try {
        const fromDate7d = new Date();
        fromDate7d.setDate(fromDate7d.getDate() - 7);
        
        const releases7d = await devOpsApi.getCombinedReleaseHistory(env, fromDate7d);
        
        // Filter 7-day data to get 24-hour data
        const fromDate24h = new Date();
        fromDate24h.setHours(fromDate24h.getHours() - 24);
        const releases24h = releases7d.filter(release => 
          release.releaseDate && release.releaseDate >= fromDate24h
        );
        
        // Fetch 30-day data
        const fromDate30d = new Date();
        fromDate30d.setDate(fromDate30d.getDate() - 30);
        const releases30d = await devOpsApi.getCombinedReleaseHistory(env, fromDate30d);
        
        return {
          env,
          releases24h,
          releases7d,
          releases30d
        };
      } catch (error) {
        console.warn(`Failed to fetch release history for ${env}:`, error);
        return {
          env,
          releases24h: [],
          releases7d: [],
          releases30d: []
        };
      }
    });

    const releaseHistoryResults = await Promise.all(releaseHistoryPromises);
    const releaseHistoryData: Record<string, CombinedReleaseHistory[]> = {};
    
    releaseHistoryResults.forEach(({ env, releases24h, releases7d, releases30d }) => {
      releaseHistoryData[`${env}_24h`] = releases24h;
      releaseHistoryData[`${env}_7d`] = releases7d;
      releaseHistoryData[`${env}_30d`] = releases30d;
    });

    setReleaseHistory(releaseHistoryData);
  }, [devOpsApi]);

  const fetchCustomDateRangeReleases = useCallback(async (fromDate: Date, toDate: Date) => {
    const customReleasePromises = ENVIRONMENTS.map(async (env) => {
      try {
        const releases = await devOpsApi.getCombinedReleaseHistory(env, fromDate);
        const filteredReleases = releases.filter(release => 
          release.releaseDate && 
          release.releaseDate >= fromDate && 
          release.releaseDate <= toDate
        );
        return { env, releases: filteredReleases };
      } catch (error) {
        console.warn(`Failed to fetch custom date range releases for ${env}:`, error);
        return { env, releases: [] };
      }
    });

    const results = await Promise.all(customReleasePromises);
    const customReleaseData: Record<string, CombinedReleaseHistory[]> = {};
    
    results.forEach(({ env, releases }) => {
      customReleaseData[`${env}_custom`] = releases;
    });

    // Update the release history state with custom data
    setReleaseHistory(prev => ({ ...prev, ...customReleaseData }));
    
    return customReleaseData;
  }, [devOpsApi]);

  const clearCustomDateRange = useCallback(() => {
    setReleaseHistory(prev => {
      const newState = { ...prev };
      ENVIRONMENTS.forEach(env => {
        delete newState[`${env}_custom`];
      });
      return newState;
    });
  }, []);

  useEffect(() => {
    fetchReleaseHistory();
  }, [fetchReleaseHistory]);

  return { releaseHistory, fetchCustomDateRangeReleases, clearCustomDateRange };
}; 